import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { getUserRoles } from '../../../utils/userRoles';
import { getOrganizationId } from '../../../utils/organization';
import { getCommunityId } from '../../../utils/community';
import { getClubId } from '../../../utils/club';
import PageShell from '../../../components/layout/PageShell';
import { buildMatchManagerManualFormatPath } from '../../../utils/matchManagerPaths';
import { normalizeId } from '../../../utils/ids';
import { fetchMatchManagerContext } from '../../../services/matchService';

// ✅ Custom breadcrumbs example:
// const crumbs = [
//   { label: 'Home', to: '/' },
//   { label: 'Community', to: `/community/${communityId}` },
//   { label: 'Match Manager', to: `/community/${communityId}/match-manager/${managerId}` },
//   { label: 'Manual Match Format' },
// ];
// <PageShell breadcrumbs={crumbs}>...</PageShell>
// <PageShell breadcrumbOverrides={{ tournamentId: tournament?.name || 'Tournament' }}>
const MatchManagerMatchFormatsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token, user } = useAuth();
  const params = useParams();
  const {
    organizationId: routeOrganizationId,
    communityId: routeCommunityId,
    clubId: routeClubId,
    individualTournamentId: routeIndividualTournamentId,
    matchManagerId: routeMatchManagerId,
    managerId: routeManagerId,
    tournamentId,
  } = params;

  const isIndividualTournamentRoute = location.pathname.startsWith('/individual-tournament/');
  const hasRouteScope = Boolean(
    routeOrganizationId ||
    routeCommunityId ||
    routeClubId ||
    routeIndividualTournamentId ||
    isIndividualTournamentRoute
  );
  const normalizedUserId = useMemo(() => normalizeId(user?._id || user?.id), [user?._id, user?.id]);
  const organizationId = useMemo(
    () => (
      isIndividualTournamentRoute
        ? ''
        : getOrganizationId(routeOrganizationId || (!hasRouteScope ? user?.organization : ''))
    ),
    [hasRouteScope, isIndividualTournamentRoute, routeOrganizationId, user?.organization]
  );
  const communityId = useMemo(
    () => (
      isIndividualTournamentRoute
        ? ''
        : getCommunityId(routeCommunityId || (!hasRouteScope ? user?.community : ''))
    ),
    [hasRouteScope, isIndividualTournamentRoute, routeCommunityId, user?.community]
  );
  const clubId = useMemo(
    () => (
      isIndividualTournamentRoute
        ? ''
        : getClubId(routeClubId || (!hasRouteScope ? user?.club : ''))
    ),
    [hasRouteScope, isIndividualTournamentRoute, routeClubId, user?.club]
  );
  const matchManagerId = normalizeId(routeMatchManagerId || routeManagerId) || normalizedUserId;
  const individualTournamentId = isIndividualTournamentRoute
    ? normalizeId(routeIndividualTournamentId || tournamentId)
    : '';
  const [routeNotice, setRouteNotice] = useState('');
  const roles = useMemo(() => getUserRoles(user), [user]);
  const canManageSchedule = useMemo(() =>
    roles.has('match_manager') ||
    roles.has('tournament_admin') ||
    roles.has('organization_admin') ||
    roles.has('community_admin') ||
    roles.has('club_admin') ||
    roles.has('individual') ||
    roles.has('individual_admin') ||
    user?.accountType === 'individual' ||
    roles.has('super_admin'), [roles, user?.accountType]);
  const managerWorkspacePath = useMemo(() => {
    if (!tournamentId) return '';
    if (communityId) {
      return `/community/${communityId}/managers/tournaments/${tournamentId}`;
    }
    if (clubId) {
      return `/club/${clubId}/managers/tournaments/${tournamentId}`;
    }
    if (organizationId) {
      return `/org/${organizationId}/managers/tournaments/${tournamentId}`;
    }
    if (individualTournamentId) {
      return `/individual-tournament/${individualTournamentId}/admin`;
    }
    return `/managers/tournaments/${tournamentId}`;
  }, [clubId, communityId, individualTournamentId, organizationId, tournamentId]);



  const matchSchedulePath = useMemo(() => {
    if (!tournamentId || !matchManagerId) return '';
    if (communityId) {
      return `/community/${communityId}/match-manager/${matchManagerId}/tournaments/${tournamentId}`;
    }
    if (clubId) {
      return `/club/${clubId}/match-manager/${matchManagerId}/tournaments/${tournamentId}`;
    }
    if (organizationId) {
      return `/org/${organizationId}/match-manager/${matchManagerId}/tournaments/${tournamentId}`;
    }
    if (individualTournamentId) {
      return `/individual-tournament/${individualTournamentId}/match-manager/${matchManagerId}/tournaments/${tournamentId}`;
    }
    return '';
  }, [clubId, communityId, individualTournamentId, matchManagerId, organizationId, tournamentId]);

  const dynamicMatchPath = matchSchedulePath ? `${matchSchedulePath}/match-formats/dynamic`.replace('/match-formats/match-formats', '/match-formats') : '';

  const manualMatchPath = useMemo(() => buildMatchManagerManualFormatPath({
    organizationId,
    communityId,
    clubId,
    individualTournamentId,
    managerId: matchManagerId,
    tournamentId,
  }), [clubId, communityId, individualTournamentId, matchManagerId, organizationId, tournamentId]);
  const buildFormatPath = (format) => {
    if (!matchSchedulePath) return '';
    if (!format || format === 'round-robin') return matchSchedulePath;
    return `${matchSchedulePath}?format=${encodeURIComponent(format)}`;
  };

  useEffect(() => {
    let cancelled = false;
    if (!token || !tournamentId || !matchSchedulePath) return undefined;
    fetchMatchManagerContext({ token, tournamentId })
      .then((context) => {
        if (cancelled) return;
        console.log('MATCH_MANAGER_ROUTE_DEBUG', {
          tournamentId,
          auctionMode: context?.auctionMode,
          teamFormationSource: context?.teamFormationSource,
          approvedTeamRegistrationCount: context?.approvedTeamRegistrationCount,
          finalizedTeamCount: context?.finalizedTeamCount,
          hasSchedule: context?.hasPublishedSchedule,
          recommendedRoute: context?.recommendedRoute,
          reason: context?.reason,
        });
        if (context?.recommendedRoute === 'matches') navigate(matchSchedulePath, { replace: true, state: { initialTab: 'matches' } });
        else if (context?.recommendedRoute === 'dynamic_scheduler' && dynamicMatchPath) navigate(dynamicMatchPath, { replace: true });
        else if (context?.recommendedRoute === 'manual_fixtures' && manualMatchPath) navigate(manualMatchPath, { replace: true });
        else if (context?.recommendedRoute === 'blocked') setRouteNotice(context?.reason || 'At least two approved teams are required before scheduling matches.');
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [dynamicMatchPath, manualMatchPath, matchSchedulePath, navigate, token, tournamentId]);

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  if (!canManageSchedule) {
    return (
      <PageShell containerClassName="p-0">
        <div className="py-4">
          <div className="alert alert-warning mb-0">Not authorized to schedule matches.</div>
        </div>
      </PageShell>
    );
  }

  const handleManualClick = () => {
    if (manualMatchPath) {
      navigate(manualMatchPath);
    }
  };

  const handleBackClick = () => {
    if (managerWorkspacePath) {
      navigate(managerWorkspacePath);
    }
  };

  return (
    <PageShell containerClassName="p-0">
    <div className="py-4 d-grid gap-4">
      <div className="d-flex flex-column flex-lg-row justify-content-lg-between align-items-lg-center gap-2">
        <div>
          <p className="eyebrow text-primary mb-1">Match manager</p>
          <h1 className="h4 mb-2">Choose a match format</h1>
          <p className="text-muted mb-0">
            Pick the scheduling format you want to build for this tournament.
          </p>
        </div>
        <div>
          <button type="button" className="btn btn-outline-secondary" onClick={handleBackClick}>
            Back to Manager workspace
          </button>
        </div>
      </div>

      {routeNotice && <div className="alert alert-warning">{routeNotice}</div>}

      <div className="row g-4">
        <div className="col-12 col-lg-6">
          <Link
            className="card h-100 border-0 shadow-sm cursor-pointer"
            to={matchSchedulePath || '#'}
            aria-disabled={!matchSchedulePath}
            onClick={(event) => {
              if (!matchSchedulePath) {
                event.preventDefault();
              }
            }}
            style={{ textDecoration: 'none' }}
          >
            <div className="card-body d-flex flex-column gap-3">
              <div className="d-flex justify-content-between align-items-start gap-2">
                <div>
                  <p className="eyebrow text-uppercase text-primary fw-semibold mb-1">League match format</p>
                  <h2 className="h5 mb-1">Round-robin scheduling</h2>
                  <p className="text-muted mb-0">
                    Build a league-style schedule for all teams with match-day caps and protected slots.
                  </p>
                </div>
                <span className="badge bg-success-subtle text-success-emphasis">Available</span>
              </div>
              <div className="d-flex gap-2 align-items-center">
                <span className="btn btn-sm btn-primary">Open schedule builder</span>
                <span className="text-muted small">Launch the match scheduling workspace</span>
              </div>
            </div>
          </Link>
        </div>
        <div className="col-12 col-lg-6">
          <Link
            className="card h-100 border-0 shadow-sm cursor-pointer"
            to={dynamicMatchPath || '#'}
            aria-disabled={!dynamicMatchPath}
            onClick={(event) => { if (!dynamicMatchPath) event.preventDefault(); }}
            style={{ textDecoration: 'none' }}
          >
            <div className="card-body d-flex flex-column gap-3">
              <div className="d-flex justify-content-between align-items-start gap-2">
                <div>
                  <p className="eyebrow text-uppercase text-primary fw-semibold mb-1">Dynamic scheduler</p>
                  <h2 className="h5 mb-1">Generate draft schedule</h2>
                  <p className="text-muted mb-0">Create a draft, edit slots inline, lock fixtures, reorder, and publish to the match list.</p>
                </div>
                <span className="badge bg-info-subtle text-info-emphasis">New</span>
              </div>
              <div className="d-flex gap-2 align-items-center"><span className="btn btn-sm btn-primary">Open dynamic scheduler</span></div>
            </div>
          </Link>
        </div>
        <div className="col-12 col-lg-6">
          <div
            className="card h-100 border-0 shadow-sm cursor-pointer"
            role="button"
            tabIndex={0}
            onClick={handleManualClick}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                handleManualClick();
              }
            }}
          >
            <div className="card-body d-flex flex-column gap-3">
              <div className="d-flex justify-content-between align-items-start gap-2">
                <div>
                  <p className="eyebrow text-uppercase text-primary fw-semibold mb-1">Manual match format</p>
                  <h2 className="h5 mb-1">Build your own fixtures</h2>
                  <p className="text-muted mb-0">
                    Add every match detail yourself, from teams and times to day labels and notes.
                  </p>
                </div>
                <span className="badge bg-warning-subtle text-warning-emphasis">Flexible</span>
              </div>
              <div className="d-flex gap-2 align-items-center">
                <span className="btn btn-sm btn-primary">Open manual editor</span>
                <span className="text-muted small">Customize matches without a generator</span>
              </div>
            </div>
          </div>
        </div>
        {[
          ['knockout', 'Knockout', 'Single elimination with automatic byes when needed.'],
          ['league_final', 'League + Final', 'League stage followed by a final between top 2.'],
          ['league_semifinal_final', 'League + Semifinal + Final', 'League stage with top 4 playoffs.'],
          ['ipl_playoffs', 'IPL-style Playoffs', 'League + Qualifier 1, Eliminator, Qualifier 2, and Final.'],
          ['group_knockout', 'Group Stage + Knockout', 'Teams split into groups, then progress to knockout.'],
          ['double_elimination', 'Double Elimination', 'Winner and loser brackets with two-loss elimination.'],
        ].map(([formatValue, title, description]) => (
          <div className="col-12 col-lg-6" key={formatValue}>
            <Link
              className="card h-100 border-0 shadow-sm cursor-pointer"
              to={buildFormatPath(formatValue)}
              style={{ textDecoration: 'none' }}
            >
              <div className="card-body d-flex flex-column gap-3">
                <div>
                  <p className="eyebrow text-uppercase text-primary fw-semibold mb-1">New format</p>
                  <h2 className="h5 mb-1">{title}</h2>
                  <p className="text-muted mb-0">{description}</p>
                </div>
                <div className="d-flex gap-2 align-items-center">
                  <span className="btn btn-sm btn-primary">Open scheduler</span>
                </div>
              </div>
            </Link>
          </div>
        ))}
      </div>
    </div>
  
    </PageShell>
  );
};

export default MatchManagerMatchFormatsPage;
